import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CalendarClock, ChevronDown, ChevronUp, Globe2, Heart, Info, Loader2, MapPin, Search, Shuffle, Signal, Timer } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { useLanguage } from "../i18n";
import { usePageMeta } from "../hooks/usePageMeta";
import { useAuth } from "../auth";
import { Reveal } from "../components/Reveal";
import KycModal from "../components/KycModal";
import PaymentMethodDialog from "../components/PaymentMethodDialog";
import PhoneGateModal from "../components/PhoneGateModal";
import ShareButton from "../components/ShareButton";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

export default function DestinationDetail() {
  const { code } = useParams();
  const { t, regionName, fmt, currency, lang } = useLanguage();
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [packages, setPackages] = useState([]);
  const [mock, setMock] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [kycOpen, setKycOpen] = useState(false);
  const pendingPkg = useRef(null);       // { p, method: "wallet" | "card" | "new_card" }
  const [payPkg, setPayPkg] = useState(null);   // pacchetto nel selettore metodo di pagamento
  const [payBusy, setPayBusy] = useState(null); // "wallet" | "card" | "new_card" | null
  const [savedCard, setSavedCard] = useState(null);
  const [phoneGate, setPhoneGate] = useState(null); // null | "certify" | "confirm"

  // 428 dal pagamento: instrada al modulo giusto (telefono/SMS ordine o dati fiscali)
  const handleGate = (e, method, p) => {
    pendingPkg.current = { p, method };
    setPayPkg(null);
    setPayBusy(null);
    const det = e.response?.data?.detail;
    // token dispositivo affidabile scaduto o revocato: lo tolgo così ricompare la spunta "ricorda"
    if (det === "order_otp_required") localStorage.removeItem("simamma_td");
    if (det === "phone_required" || det === "order_otp_required") {
      setPhoneGate(det === "phone_required" ? "certify" : "confirm");
    } else {
      setKycOpen(true);
    }
  };
  const deliverDateRef = useRef(null);

  const isLocal = code.length === 2;

  const [highlights, setHighlights] = useState(null);
  const [shown, setShown] = useState([]);
  const [wishlist, setWishlist] = useState(new Set());
  const [friendPct, setFriendPct] = useState(0);
  const [winbackPct, setWinbackPct] = useState(0);
  const [deliverAt, setDeliverAt] = useState("");  // "" = subito, altrimenti YYYY-MM-DD
  const [promo, setPromo] = useState("");            // codice sconto inserito
  const [promoState, setPromoState] = useState(null); // null | {valid, code, kind, value} | {valid:false}
  const [promoBusy, setPromoBusy] = useState(false);
  const [departAt, setDepartAt] = useState("");    // "" = non indicata, altrimenti YYYY-MM-DD
  const [returnAt, setReturnAt] = useState("");    // "" = non indicato
  const tripDays = departAt && returnAt && returnAt >= departAt
    ? Math.round((new Date(returnAt) - new Date(departAt)) / 86400000) + 1
    : 0;
  // Piano consigliato: il più economico che copre tutta la durata del viaggio
  // (se nessuno copre, quello con la validità più lunga)
  const idealId = useMemo(() => {
    if (!tripDays || !packages.length) return null;
    const covering = [...packages].sort((a, b) => a.price - b.price)
      .filter((p) => Number(p.validity) >= tripDays);
    const best = covering[0] || [...packages].sort((a, b) => Number(b.validity) - Number(a.validity))[0];
    return best?.package_id || null;
  }, [tripDays, packages]);

  // Destinazione mondiale: i giga valgono ovunque — va detto forte e chiaro
  const isGlobal = packages.some((p) => p.operator_type === "global" || Number(p.country_count) >= 80);
  const worldCountries = packages.reduce((m, p) => Math.max(m, Number(p.country_count) || 0), 0);

  // Copertura interattiva: lista sfogliabile dei paesi coperti (con ricerca)
  const [coverage, setCoverage] = useState(null);
  const [covOpen, setCovOpen] = useState(false);
  const [covQ, setCovQ] = useState("");
  useEffect(() => {
    if (!isGlobal) { setCoverage(null); return; }
    api.get(`/destinations/${code}/coverage`).then(({ data }) => setCoverage(data.data || [])).catch(() => setCoverage([]));
  }, [isGlobal, code]);
  const covFiltered = (coverage || []).filter((c) => c.title.toLowerCase().includes(covQ.trim().toLowerCase()));

  const dateInputValue = (d) => {
    const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 10);
  };
  const today = () => dateInputValue(new Date());
  const tomorrow = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return dateInputValue(d);
  };
  const openDeliverPicker = () => {
    const el = deliverDateRef.current;
    if (!el) return;
    if (typeof el.showPicker === "function") el.showPicker();
    else el.focus();
  };
  const updateDeliverAt = (value) => {
    // Oggi equivale a consegna immediata: il backend continua a ricevere deliver_at vuoto.
    setDeliverAt(value > today() ? value : "");
  };

  useEffect(() => {
    if (!user) return;
    api.get("/wishlist").then(({ data }) => setWishlist(new Set((data.data || []).map((w) => w.package_id)))).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user) { setSavedCard(null); return; }
    api.get("/payments/saved-card").then(({ data }) => setSavedCard(data.data || { saved: false })).catch(() => setSavedCard({ saved: false }));
  }, [user]);

  useEffect(() => {
    if (!user) { setFriendPct(0); setWinbackPct(0); return; }
    api.get("/referrals/friend-discount").then(({ data }) => {
      setFriendPct(data.data?.available ? data.data.pct : 0);
      setWinbackPct(data.data?.winback_pct || 0);
    }).catch(() => {});
  }, [user]);

  const toggleWish = async (p) => {
    if (!user) {
      navigate(`/account?next=${encodeURIComponent(location.pathname)}`);
      return;
    }
    const has = wishlist.has(p.package_id);
    try {
      if (has) {
        await api.delete(`/wishlist/${encodeURIComponent(p.package_id)}`);
        toast.success(t("wish.removed"));
      } else {
        await api.post("/wishlist", { package_id: p.package_id });
        toast.success(t("wish.added"));
      }
      setWishlist((s) => {
        const n = new Set(s);
        if (has) n.delete(p.package_id); else n.add(p.package_id);
        return n;
      });
    } catch {
      toast.error(t("misc.error"));
    }
  };

  useEffect(() => {
    if (!isLocal) return undefined;
    let alive = true;
    const load = () => api.get(`/destinations/${code}/highlights?lang=${lang}`)
      .then(({ data }) => { if (alive) setHighlights(data.data || []); })
      .catch(() => { if (alive) setHighlights([]); });
    load();
    const retry = setTimeout(load, 20000);
    return () => { alive = false; clearTimeout(retry); };
  }, [code, isLocal, lang]);

  useEffect(() => {
    if (highlights?.length) setShown([...highlights].sort(() => Math.random() - 0.5).slice(0, 4));
  }, [highlights]);

  const reshuffle = () => setShown([...(highlights || [])].sort(() => Math.random() - 0.5).slice(0, 4));
  const imgSrc = (u) => (u?.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

  useEffect(() => {
    if (!user || loading) return;
    try {
      const pending = JSON.parse(sessionStorage.getItem("atlas_pending_buy") || "null");
      if (pending && pending.path === location.pathname) {
        sessionStorage.removeItem("atlas_pending_buy");
        const pkg = packages.find((x) => x.package_id === pending.package_id);
        if (pkg) startBuy(pkg);
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading, packages]);

  const loadPackages = () => {
    const query = isLocal ? `country=${code.toUpperCase()}` : `destination=${encodeURIComponent(code)}`;
    return api.get(`/packages?${query}`).then(({ data }) => {
      setPackages(data.data || []);
      setMock(!!data.meta?.mock);
      return data.data || [];
    }).catch(() => { setPackages([]); return []; });
  };

  useEffect(() => {
    setLoading(true);
    loadPackages().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, isLocal]);

  // Il catalogo può essersi aggiornato mentre il cliente era nel giro SMS (sync schedulata
  // o riavvio server): ricarica la lista e riprova UNA volta se il pacchetto esiste ancora.
  const pkgRetried = useRef(new Set());
  const handlePkgNotFound = async (p, retryFn) => {
    const fresh = await loadPackages();
    const p2 = fresh.find((x) => x.package_id === p.package_id);
    if (p2 && !pkgRetried.current.has(p.package_id)) {
      pkgRetried.current.add(p.package_id);
      retryFn(p2);
    } else {
      pendingPkg.current = null;
      toast.error(t("pay.pkgGone"));
    }
  };

  const title = isLocal ? regionName(code.toUpperCase()) : (packages[0]?.region || packages[0]?.operator_title || code);
  const network = packages.find((p) => p.network)?.network;
  const otype = packages[0]?.operator_type;
  const seoBase = isLocal ? "seo.detail" : otype === "global" ? "seo.detailGlob" : "seo.detailReg";
  usePageMeta(`${seoBase}Title`, `${seoBase}Desc`, { c: title });

  const buyWallet = async (p) => {
    if (!user) return;
    setPayBusy("wallet");
    try {
      await api.post("/wallet/order", { package_id: p.package_id,
        ...(deliverAt ? { deliver_at: deliverAt } : {}),
        ...(departAt ? { departure_date: departAt } : {}),
        ...(returnAt ? { return_date: returnAt } : {}) });
      toast.success(t("wallet.orderDone"));
      setPayPkg(null);
      setPayBusy(null);
      await checkAuth();
      navigate("/account?tab=esims");
    } catch (e) {
      const st = e.response?.status;
      if (st === 428) {
        handleGate(e, "wallet", p);
      } else if (st === 402) {
        toast.error(t("wallet.insufficient"));
      } else if (e.response?.data?.detail === "identity_verification_required") {
        toast.error(t("id.blockedCheckout"));
        setPayPkg(null);
        navigate("/account?tab=account");
      } else if (e.response?.data?.detail === "Package not found") {
        setPayBusy(null);
        handlePkgNotFound(p, buyWallet);
        return;
      } else {
        toast.error(e.response?.data?.detail || t("misc.error"));
      }
      setPayBusy(null);
    }
  };

  const buySavedCard = async (p) => {
    if (!user) return;
    setPayBusy("card");
    try {
      const { data } = await api.post("/payments/pay-with-saved-card", {
        package_id: p.package_id,
        ...(deliverAt ? { deliver_at: deliverAt } : {}),
        ...(departAt ? { departure_date: departAt } : {}),
        ...(returnAt ? { return_date: returnAt } : {}),
        ...(promoState?.valid ? { promo_code: promoState.code } : {}),
      });
      if (data.status === "requires_action") {
        // 3DS non gestito in-page sul web: si prosegue col checkout Stripe completo
        toast.info(t("pay.cardAction"));
        setPayBusy(null);
        checkoutStripe(p);
        return;
      }
      toast.success(t("pay.orderDoneCard"));
      setPayPkg(null);
      setPayBusy(null);
      await checkAuth();
      navigate("/account?tab=esims");
    } catch (e) {
      const st = e.response?.status;
      const det = e.response?.data?.detail;
      if (st === 428) {
        handleGate(e, "card", p);
      } else if (st === 402) {
        toast.error(t("pay.cardDeclined"));
      } else if (det === "identity_verification_required") {
        toast.error(t("id.blockedCheckout"));
        setPayPkg(null);
        navigate("/account?tab=account");
      } else if (det === "no_saved_card") {
        setSavedCard({ saved: false });
      } else if (det === "Package not found") {
        setPayBusy(null);
        handlePkgNotFound(p, buySavedCard);
        return;
      } else {
        toast.error(det === "invalid_promo_code" ? t("promo.invalid") : det === "provisioning_failed_refunded" ? t("pay.failedProv") : det === "airalo_credit_pending" ? t("pay.creditPending") : (det || t("misc.error")));
      }
      setPayBusy(null);
    }
  };

  const checkoutStripe = async (p) => {
    setBusy(p.package_id);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: p.package_id,
        origin_url: window.location.origin,
        currency,
        lang,
        ...(deliverAt ? { deliver_at: deliverAt } : {}),
        ...(departAt ? { departure_date: departAt } : {}),
        ...(returnAt ? { return_date: returnAt } : {}),
        ...(promoState?.valid ? { promo_code: promoState.code } : {}),
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      if (e.response?.status === 428) {
        handleGate(e, "new_card", p);
      } else {
        const det = e.response?.data?.detail;
        if (det === "identity_verification_required") {
          toast.error(t("id.blockedCheckout"));
          setPayPkg(null);
          navigate("/account?tab=account");
        } else if (det === "Package not found") {
          setBusy(null);
          setPayBusy(null);
          handlePkgNotFound(p, checkoutStripe);
          return;
        } else {
          toast.error(det === "invalid_deliver_date" ? t("misc.invalidDate") : det === "invalid_promo_code" ? t("promo.invalid") : (det || t("misc.error")));
        }
      }
      setBusy(null);
      setPayBusy(null);
    }
  };

  const startBuy = (p) => {
    if (!user) {
      try {
        sessionStorage.setItem("atlas_pending_buy", JSON.stringify({ package_id: p.package_id, path: location.pathname }));
      } catch { /* ignore */ }
      navigate(`/account?next=${encodeURIComponent(location.pathname)}`);
      return;
    }
    // Se ci sono alternative al checkout Stripe (plafond o carta salvata) il cliente sceglie prima
    if ((user.wallet_balance || 0) > 0 || savedCard?.saved) {
      setPayPkg(p);
      return;
    }
    checkoutStripe(p);
  };

  const onPickMethod = (method) => {
    const p = payPkg;
    if (!p) return;
    if (method === "wallet") buyWallet(p);
    else if (method === "card") buySavedCard(p);
    else checkoutStripe(p);
  };

  const retryPending = () => {
    const pend = pendingPkg.current;
    if (!pend) return;
    if (pend.method === "wallet") buyWallet(pend.p);
    else if (pend.method === "card") buySavedCard(pend.p);
    else checkoutStripe(pend.p);
  };

  return (
    <div data-testid="package-detail" className="px-4 sm:px-6 lg:px-12 max-w-4xl mx-auto py-12 sm:py-16">
      <Reveal>
        <Link to="/destinations" data-testid="back-to-destinations" className="inline-flex items-center gap-2 font-mono-t text-xs font-bold uppercase tracking-[0.2em] text-slate-400 hover:text-[#FF6B35] transition-colors">
          <ArrowLeft className="w-4 h-4" /> {t("dest.back")}
        </Link>
        {isLocal && (
          <img
            src={`/illustrations/${code.toLowerCase()}.png`}
            alt=""
            onError={(e) => { e.currentTarget.style.display = "none"; }}
            className="mt-8 w-full h-44 sm:h-60 object-cover rounded-[2rem] shadow-lg shadow-orange-100"
          />
        )}
        <div className="mt-8 flex items-center gap-5 sm:gap-7">
          {isLocal && (
            <img
              src={`https://flagcdn.com/w160/${code.toLowerCase()}.png`}
              alt={code}
              className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover border-4 border-white shadow-lg shrink-0"
            />
          )}
          <div>
            <div className="font-mono-t text-[11px] font-bold uppercase tracking-[0.3em] text-[#FF6B35] mb-2">
              {isLocal ? code.toUpperCase() : t("cat.regional") + " / " + t("cat.global")}
            </div>
            <h1 className="font-display font-extrabold tracking-tight text-4xl sm:text-6xl leading-none text-slate-900">{title}</h1>
          </div>
        </div>
        <div className="mt-6 flex flex-wrap gap-3">
          {network && (
            <span className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-4 py-2 text-sm text-slate-600 shadow-sm">
              <Signal className="w-4 h-4 text-[#0D9488]" /> {isGlobal ? t("dest.worldNetworks") : network}
            </span>
          )}
          <span className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-4 py-2 text-sm text-slate-600 shadow-sm">
            <Timer className="w-4 h-4 text-[#0D9488]" /> {t("dest.activation")}: {t("dest.instant")}
          </span>
          <ShareButton testid="share-destination" url={location.pathname} title={`SIMAMMA — ${title}`}
            text={`${t("share.textDest")} ${title}`} />
        </div>
      </Reveal>

      {mock && (
        <div data-testid="demo-catalog-banner" className="mt-8 rounded-2xl border border-amber-300/60 bg-amber-50 px-5 py-3 text-sm text-slate-600">
          <span className="font-mono-t text-[10px] font-bold uppercase tracking-[0.25em] text-[#FF6B35] mr-3">{t("misc.demo")}</span>
          {t("misc.demoSub")}
        </div>
      )}

      {isLocal && highlights === null && (
        <div data-testid="highlights-loading" className="mt-14 grid sm:grid-cols-2 gap-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-56 rounded-3xl bg-white border border-slate-200/80 animate-pulse" />
          ))}
        </div>
      )}

      {isLocal && shown.length > 0 && (
        <section data-testid="highlights-section" className="mt-14">
          <Reveal>
            <div className="font-mono-t text-[11px] font-bold uppercase tracking-[0.3em] text-[#FF6B35] mb-2">{t("high.eyebrow")}</div>
            <h2 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl text-slate-900">{title}</h2>
          </Reveal>
          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            {shown.map((h, i) => (
              <Reveal key={h.name} delay={Math.min(i, 3) * 0.05}>
                <div data-testid={`highlight-card-${i}`} className="rounded-3xl overflow-hidden bg-white border border-slate-200/80 shadow-sm hover:shadow-md transition-shadow duration-200">
                  {h.image ? (
                    <img src={imgSrc(h.image)} alt={h.name} loading="lazy" className="h-44 w-full object-cover" />
                  ) : (
                    <div className="h-44 w-full bg-[#FAF5EE] flex items-center justify-center">
                      <MapPin className="w-8 h-8 text-[#FF6B35]/40" />
                    </div>
                  )}
                  <div className="p-5">
                    <div className="font-display font-bold text-lg text-slate-900">{h.name}</div>
                    <p className="mt-1 text-sm text-slate-500 leading-relaxed">{h.tip}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <div className="mt-6 flex flex-wrap items-center justify-between gap-4 rounded-3xl bg-[#FAF5EE] border border-orange-100 px-6 py-5">
              <div>
                <div data-testid="highlights-ready" className="font-display font-extrabold text-xl text-slate-900">{t("high.ready")}</div>
                <div className="text-sm text-slate-500 mt-0.5">{t("high.readySub")}</div>
              </div>
              {highlights.length > 4 && (
                <button data-testid="highlights-shuffle" onClick={reshuffle}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-[0.15em] text-slate-600 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors">
                  <Shuffle className="w-3.5 h-3.5" /> {t("high.shuffle")}
                </button>
              )}
            </div>
          </Reveal>
        </section>
      )}

      <div className="mt-12">
        {isGlobal && (
          <div data-testid="world-banner" className="mb-8 rounded-3xl bg-[#0C0D0E] px-6 sm:px-8 py-7 flex flex-wrap items-center gap-5">
            <span className="w-14 h-14 rounded-full bg-[#FF6B35]/15 text-[#FF6B35] flex items-center justify-center shrink-0">
              <Globe2 className="w-7 h-7" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="font-mono-t text-[10px] font-bold uppercase tracking-[0.3em] text-[#FF6B35]">{t("dest.worldKicker")}</div>
              <div className="font-display font-extrabold text-2xl text-white tracking-tight mt-1">{t("dest.worldTitle").replace("{n}", worldCountries)}</div>
              <p className="mt-1.5 text-sm text-white/60 max-w-xl">{t("dest.worldSub")}</p>
            </div>
          </div>
        )}
        {isGlobal && coverage && coverage.length > 0 && (
          <div data-testid="coverage-section" className="mb-8 rounded-3xl border border-slate-200/80 bg-white/70">
            <button data-testid="coverage-toggle" onClick={() => setCovOpen(!covOpen)}
              className="w-full flex items-center justify-between gap-4 px-6 sm:px-8 py-5 text-left">
              <span className="font-display font-bold text-lg text-slate-900">
                {t("dest.coverageTitle").replace("{n}", coverage.length)}
              </span>
              {covOpen ? <ChevronUp className="w-5 h-5 text-slate-400 shrink-0" /> : <ChevronDown className="w-5 h-5 text-slate-400 shrink-0" />}
            </button>
            {covOpen && (
              <div className="px-6 sm:px-8 pb-6">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input data-testid="coverage-search" value={covQ} onChange={(e) => setCovQ(e.target.value)}
                    placeholder={t("dest.coverageSearch")}
                    className="w-full rounded-full border border-slate-200 bg-white pl-12 pr-5 py-2.5 text-sm outline-none focus:border-[#FF6B35] transition-colors" />
                </div>
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 max-h-96 overflow-y-auto pr-1">
                  {covFiltered.length === 0 ? (
                    <p className="col-span-full py-4 text-sm text-slate-400">{t("dest.coverageNone")}</p>
                  ) : covFiltered.map((c) => (
                    <Link key={c.code} to={`/destination/${c.code.toLowerCase()}`} data-testid={`coverage-item-${c.code}`}
                      className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors truncate">
                      {c.title}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        <h2 className="font-mono-t text-[11px] font-bold uppercase tracking-[0.3em] text-slate-400 mb-5">{t("dest.plans")}</h2>
        {friendPct > 0 && (
          <p data-testid="friend-discount-note" className="-mt-3 mb-5 text-xs text-emerald-700">{t("ref.friendNote")}</p>
        )}
        {loading ? (
          <div data-testid="packages-loading" className="py-20 text-center font-mono-t text-xs font-bold uppercase tracking-[0.3em] text-slate-400">{t("misc.loading")}</div>
        ) : (
          <div data-testid="package-picker-container" className="space-y-3">
            <div data-testid="delivery-picker" className="rounded-2xl bg-white border border-slate-200/80 px-5 sm:px-7 py-5 shadow-sm">
              <div className="flex items-center gap-2 font-mono-t text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-3">
                <CalendarClock className="w-4 h-4 text-[#FF6B35]" /> {t("dest.deliverQ")}
              </div>
              <div className="flex flex-col lg:flex-row lg:flex-nowrap lg:items-center gap-3">
                <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-[#FAF5EE]/70 px-3 py-2 min-w-0">
                  <label htmlFor="deliver-date" className="font-mono-t text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 whitespace-nowrap">
                    {t("dest.deliveryLabel")}
                  </label>
                  <input id="deliver-date" ref={deliverDateRef} data-testid="deliver-date" type="date" value={deliverAt || today()} min={today()}
                    onChange={(e) => updateDeliverAt(e.target.value)}
                    className="w-[136px] bg-transparent text-sm text-slate-700 outline-none focus:text-[#FF6B35] transition-colors" />
                  <button type="button" data-testid="deliver-change-date" onClick={openDeliverPicker}
                    aria-label={t("dest.changeDate")}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-white border border-slate-200 px-3 py-1.5 font-mono-t text-[9px] font-bold uppercase tracking-[0.14em] text-slate-600 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors">
                    <CalendarClock className="w-3.5 h-3.5" /> {t("dest.changeDate")}
                  </button>
                  {deliverAt && (
                    <button type="button" data-testid="deliver-now" onClick={() => setDeliverAt("")}
                      className="shrink-0 rounded-full bg-[#1E293B] text-white px-3 py-1.5 font-mono-t text-[9px] font-bold uppercase tracking-[0.14em] hover:bg-[#FF6B35] transition-colors">
                      {t("dest.deliverNow")}
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2 rounded-full border border-slate-200 px-3 py-2 min-w-0">
                  <label htmlFor="depart-date" className="font-mono-t text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 whitespace-nowrap">{t("dest.departLabel")}</label>
                  <input id="depart-date" data-testid="depart-date" type="date" value={departAt} min={tomorrow()}
                    onChange={(e) => setDepartAt(e.target.value)}
                    className="w-[136px] bg-transparent text-sm text-slate-700 outline-none focus:text-[#FF6B35] transition-colors" />
                </div>
                {departAt && (
                  <div className="flex items-center gap-2 rounded-full border border-slate-200 px-3 py-2 min-w-0 animate-fade-in">
                    <label htmlFor="return-date" className="font-mono-t text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 whitespace-nowrap">{t("dest.returnLabel")}</label>
                    <input id="return-date" data-testid="return-date" type="date" value={returnAt} min={departAt}
                      onChange={(e) => setReturnAt(e.target.value)}
                      className="w-[136px] bg-transparent text-sm text-slate-700 outline-none focus:text-[#FF6B35] transition-colors" />
                  </div>
                )}
              </div>
              <p data-testid="delivery-charge-note" className="mt-3 flex items-start gap-2 text-xs leading-relaxed text-slate-500">
                <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#0D9488]" />
                <span>{t("dest.chargeNote")}</span>
              </p>
            </div>
            {/* Codice sconto (campagne Admin → Marketing): validazione live, sconto applicato da Stripe al checkout */}
            <div data-testid="promo-box" className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-dashed border-slate-300 bg-white/70 px-5 py-4">
              <span className="font-mono-t text-[10px] font-bold uppercase tracking-[0.22em] text-slate-400">{t("promo.label")}</span>
              <input data-testid="promo-input" value={promo} placeholder={t("promo.placeholder")}
                onChange={(e) => { setPromo(e.target.value.toUpperCase()); setPromoState(null); }}
                className="w-44 rounded-xl border border-slate-200 bg-white px-4 py-2 font-mono-t text-sm font-bold uppercase outline-none focus:border-[#FF6B35]" />
              <button type="button" data-testid="promo-apply" disabled={promoBusy || !promo.trim()}
                onClick={async () => {
                  setPromoBusy(true);
                  try {
                    const { data } = await api.post("/payments/promo/validate", { code: promo.trim() });
                    setPromoState(data.valid ? data : { valid: false });
                    if (!data.valid) toast.error(t("promo.invalid"));
                  } catch (e) {
                    if (e.response?.status === 401) setPromoState({ valid: true, code: promo.trim().toUpperCase(), kind: "percent", value: 0 });
                    else toast.error(t("misc.error"));
                  } finally {
                    setPromoBusy(false);
                  }
                }}
                className="rounded-full bg-[#1E293B] text-white px-5 py-2 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-[#FF6B35] transition-colors disabled:opacity-50">
                {promoBusy ? "…" : t("promo.apply")}
              </button>
              {promoState?.valid && (
                <span data-testid="promo-applied" className="text-sm font-bold text-emerald-600">
                  {t("promo.applied")}: {promoState.code}{promoState.value ? (promoState.kind === "percent" ? ` -${promoState.value}%` : ` -€${promoState.value}`) : ""}
                </span>
              )}
              {promoState && !promoState.valid && (
                <span data-testid="promo-invalid" className="text-sm text-rose-600">{t("promo.invalid")}</span>
              )}
            </div>
            {packages.map((p, i) => (
              <Reveal key={p.package_id} delay={Math.min(i, 8) * 0.04}>
                <div data-testid={`package-card-${p.package_id}`}
                  className="flex flex-wrap items-center gap-x-4 gap-y-3 rounded-2xl bg-white border border-slate-200/80 px-4 sm:px-7 py-4 sm:py-5 shadow-sm hover:border-[#FF6B35]/50 hover:shadow-md transition-all duration-200">
                  <span className="rounded-full bg-[#D1FAE5] text-[#0D9488] px-3 py-1 font-mono-t text-[10px] font-bold uppercase tracking-[0.15em]">
                    {p.validity} {t("dest.days")}
                  </span>
                  {isGlobal && (
                    <span data-testid={`world-badge-${p.package_id}`}
                      className="inline-flex items-center gap-1 rounded-full bg-[#0C0D0E] text-white px-3 py-1 font-mono-t text-[9px] font-bold uppercase tracking-[0.12em]">
                      <Globe2 className="w-3 h-3" /> {t("dest.worldBadge")}
                    </span>
                  )}
                  {tripDays > 0 && p.package_id === idealId && (
                    <span data-testid={`ideal-badge-${p.package_id}`}
                      className="rounded-full bg-emerald-600 text-white px-3 py-1 font-mono-t text-[9px] font-bold uppercase tracking-[0.12em]">
                      ★ {t("dest.ideal")}
                    </span>
                  )}
                  {tripDays > 0 && Number(p.validity) < tripDays && (
                    <span data-testid={`trip-short-${p.package_id}`}
                      className="rounded-full bg-amber-50 border border-amber-200 text-amber-700 px-3 py-1 font-mono-t text-[9px] font-bold uppercase tracking-[0.12em]">
                      {t("dest.tripTooLong").replace("{v}", p.validity).replace("{d}", tripDays)}
                    </span>
                  )}
                  <div className="font-display font-extrabold text-lg sm:text-2xl text-slate-900">
                    {p.is_unlimited ? t("dest.unlimited") : p.data}
                  </div>
                  {p.network && <div className="hidden md:block text-sm text-slate-400">{isGlobal ? t("dest.worldNetworks") : p.network}</div>}
                  <div className="ml-auto flex items-center gap-3 sm:gap-5 flex-wrap justify-end w-full sm:w-auto">
                    <button
                      data-testid={`wish-toggle-${p.package_id}`}
                      onClick={() => toggleWish(p)}
                      aria-label={t("wish.toggle")}
                      className={`shrink-0 min-h-[44px] min-w-[44px] inline-flex items-center justify-center transition-colors ${wishlist.has(p.package_id) ? "text-[#FF6B35]" : "text-slate-300 hover:text-[#FF6B35]"}`}>
                      <Heart className={`w-5 h-5 ${wishlist.has(p.package_id) ? "fill-current" : ""}`} />
                    </button>
                    {friendPct > 0 ? (
                      <span className="flex items-center gap-2 flex-wrap justify-end">
                        <span className="text-right leading-tight">
                          <span className="block font-mono-t text-[11px] text-slate-400 line-through">{fmt(p.price)}</span>
                          <span className="font-mono-t font-bold text-base sm:text-lg text-[#FF6B35]">{fmt(p.price * (1 - friendPct / 100))}</span>
                        </span>
                        <span data-testid={`friend-discount-${p.package_id}`} className="rounded-full bg-emerald-50 text-emerald-700 px-2.5 py-1 font-mono-t text-[9px] font-bold uppercase tracking-widest whitespace-nowrap">
                          -{friendPct}% {t("ref.friendBadge")}
                        </span>
                      </span>
                    ) : winbackPct > 0 ? (
                      <span className="flex items-center gap-2 flex-wrap justify-end">
                        <span className="text-right leading-tight">
                          <span className="block font-mono-t text-[11px] text-slate-400 line-through">{fmt(p.price)}</span>
                          <span className="font-mono-t font-bold text-base sm:text-lg text-[#FF6B35]">{fmt(p.price * (1 - winbackPct / 100))}</span>
                        </span>
                        <span data-testid={`winback-discount-${p.package_id}`} className="rounded-full bg-amber-50 text-amber-700 px-2.5 py-1 font-mono-t text-[9px] font-bold uppercase tracking-widest whitespace-nowrap">
                          -{winbackPct}% {t("dest.winbackBadge")}
                        </span>
                      </span>
                    ) : (
                      <span className="font-mono-t font-bold text-base sm:text-lg text-[#FF6B35]">{fmt(p.price)}</span>
                    )}
                    <button
                      data-testid={`package-tier-button-${p.data || p.package_id}`}
                      onClick={() => startBuy(p)}
                      disabled={busy === p.package_id}
                      className="rounded-full bg-[#FF6B35] text-white px-6 py-2.5 min-h-[44px] w-full sm:w-auto font-mono-t text-[11px] font-bold uppercase tracking-[0.15em] hover:bg-[#E8551F] transition-colors disabled:opacity-60 flex items-center justify-center gap-2 shadow-sm shadow-orange-200 text-center leading-snug max-w-full">
                      {busy === p.package_id && <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />}
                      {user ? t("dest.buy") : t("dest.loginToBuy")}
                    </button>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        )}
      </div>

      <div className="mt-12 flex items-center gap-6 font-mono-t text-[10px] font-bold uppercase tracking-[0.25em] text-slate-400">
        <span>{t("misc.secure")} · Stripe</span>
        <span>{t("misc.instant")}</span>
      </div>

      <section data-testid="faq-accordion" className="mt-16">
        <Reveal>
          <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-slate-900 mb-6">{t("faq.title")}</h2>
          <Accordion type="single" collapsible className="space-y-3">
            {[1, 2, 3, 4, 5].map((n) => (
              <AccordionItem key={n} value={`faq-${n}`} className="rounded-2xl bg-white border border-slate-200/80 px-6 shadow-sm">
                <AccordionTrigger data-testid={`faq-question-${n}`} className="font-display font-semibold text-left text-slate-900 hover:no-underline">
                  {t(`faq.q${n}`)}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed">
                  {t(`faq.a${n}`)}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </section>

      <PaymentMethodDialog
        open={!!payPkg}
        onOpenChange={(v) => { if (!v) { setPayPkg(null); setPayBusy(null); } }}
        title={payPkg ? `${payPkg.is_unlimited ? t("dest.unlimited") : payPkg.data}${payPkg.validity ? ` · ${payPkg.validity} ${t("dest.days")}` : ""}` : ""}
        priceLabel={payPkg ? fmt(payPkg.price) : ""}
        walletBalance={user?.wallet_balance || 0}
        card={savedCard}
        busy={payBusy || (payPkg && busy === payPkg.package_id ? "new_card" : null)}
        onPick={onPickMethod}
      />
      <PhoneGateModal open={!!phoneGate} mode={phoneGate || "confirm"}
        onOpenChange={(v) => { if (!v) setPhoneGate(null); }}
        onDone={() => { setPhoneGate(null); checkAuth(); retryPending(); }} />
      <KycModal open={kycOpen} onOpenChange={setKycOpen} onDone={retryPending} />
    </div>
  );
}
